import {Component} from '@angular/core';
import {WalletToWalletPaymentReceipt} from "../models/payment/walletToWalletPaymentReceipt";
import {PaymentBackUrlParamsHandlerService} from "../subscription-payment/payment-back-url-params-handler.service";
import {PaymentTransactionService} from "../service/payment-transaction.service";
import {ActivatedRoute, Params, Router} from "@angular/router";
import {MockupDataModel} from "../qr-copy-message/mockup-data.model";
import {SnackBarUtil} from "../qr-copy-message/snack-bar-util.service";
import {SUCCESS_PAYMENT_TRANSACTION_MESSAGE} from "../subscription-payment/subscription-payment.constant";
import {Order} from "../models/Order";
import {OrdersService} from "../service/orders.service";
import {TranslateService} from "@ngx-translate/core";
import {LANG} from "../constants/local-storage.contant";
import {Language} from "../models/language";
import {PaymentReceiptType} from "../enums/payment-receipt-type.enum";
import { Location } from '@angular/common';
import {CookieUtils} from "../utils/cookie.utils";
import {FastshiftService} from "../service/fastshift.service";
import {PaymentTransactionResponse} from "../interface/paymentTransactionResponse";
import {PaymentStatus} from "../enums/paymentStatus";
import {FeeService} from "../service/fee.service";

@Component({
  selector: 'app-payment-receipt',
  templateUrl: './payment-receipt.component.html',
  styleUrl: './payment-receipt.component.scss'
})
export class PaymentReceiptComponent {
  private static readonly PENDING_FEE_PAYMENT_STORAGE_KEY = 'pending_fee_payment_context';
  private static readonly PAYMENT_RECEIPT_CONTEXT_KEY = 'payment_receipt_context';
  private static readonly FEE_PAYMENT_CONTEXT = 'fee';

  protected readonly SUCCESS_PAYMENT_RESPONSE = SUCCESS_PAYMENT_TRANSACTION_MESSAGE
  protected readonly FAILED_PAYMENT_RESPONSE = "Payment failed. Please check your details and try again.";
  protected isFeePayment: boolean = false;

  protected isWalletToWallet: boolean = true;

  public static loading: boolean = true;

  public static paymentReceipt: WalletToWalletPaymentReceipt;
  public static productBuyingResponse: Order | null = null;
  public static pendingOrderNumber: string | null = null;
  public pendingStatus: PaymentStatus | null = null;
  private backUrl: string | null = null;

  constructor(
    private paymentParamsHandler: PaymentBackUrlParamsHandlerService,
    private paymentTransactionService: PaymentTransactionService,
    private snackBarUtil: SnackBarUtil,
    private route: ActivatedRoute,
    private router: Router,
    private orderService: OrdersService,
    private translateService: TranslateService,
    private location: Location,
    private fastshiftService: FastshiftService,
    private feeService: FeeService
  ) {
    this.translateService.use(CookieUtils.getCookie(LANG) || Language.HY);
    const navState = this.router.getCurrentNavigation()?.extras.state;
    const receiptFromExtras: WalletToWalletPaymentReceipt | undefined | null = navState?.['receipt'];
    const contextFromState = navState?.['paymentContext'];
    const contextFromStorage = sessionStorage.getItem(PaymentReceiptComponent.PAYMENT_RECEIPT_CONTEXT_KEY);
    this.isFeePayment = contextFromState === PaymentReceiptComponent.FEE_PAYMENT_CONTEXT
      || contextFromStorage === PaymentReceiptComponent.FEE_PAYMENT_CONTEXT;
    sessionStorage.removeItem(PaymentReceiptComponent.PAYMENT_RECEIPT_CONTEXT_KEY);
    this.backUrl = navState?.['backUrl'] ?? sessionStorage.getItem('fee_back_url') ?? null;
    if(receiptFromExtras) {
      this.paymentReceipt = receiptFromExtras;
      this.loading = false;
      this.processPendingFeePayment();
    } else {
      this.getDataWithHandlingQueryParams();
    }
  }

  get paymentReceipt() {
    return PaymentReceiptComponent.paymentReceipt;
  }

  set paymentReceipt(receipt: WalletToWalletPaymentReceipt) {
    PaymentReceiptComponent.paymentReceipt = receipt;
  }

  get productBuyingResponse(): Order | null {
    return PaymentReceiptComponent.productBuyingResponse;
  }

  set productBuyingResponse(response: Order) {
    PaymentReceiptComponent.productBuyingResponse = response;
  }

  get loading() {
    return PaymentReceiptComponent.loading;
  }

  set loading(value: boolean) {
    PaymentReceiptComponent.loading = value;
  }

  get pendingOrderNumber(): string | null {
    return PaymentReceiptComponent.pendingOrderNumber;
  }

  set pendingOrderNumber(value: string | null) {
    PaymentReceiptComponent.pendingOrderNumber = value;
  }


  get buyerFieldTitle() {
    if (!this.paymentReceipt || !this.paymentReceipt.cardName) {
      return this.translateService.instant('buyerFieldTitle.cardholderName');
    }
    return this.hasNumber(this.paymentReceipt.cardName)
      ? this.translateService.instant('buyerFieldTitle.buyer')
      : this.translateService.instant('buyerFieldTitle.cardholderName');
  }

  get successMessageKey(): string {
    return this.isFeePayment ? 'payment.success_message' : 'payment.transfer_success_message';
  }

  get transferTitleKey(): string {
    return this.isFeePayment ? 'payment_receipt.personal_transfer' : 'payment_receipt.transfer_payment';
  }

  goBack() {
    sessionStorage.removeItem('fee_back_url');
    sessionStorage.removeItem(PaymentReceiptComponent.PAYMENT_RECEIPT_CONTEXT_KEY);
    if (this.backUrl) {
      this.router.navigateByUrl(this.backUrl);
    } else {
      this.router.navigate(['/cards']);
    }
  }

  private getDataWithHandlingQueryParams() {
    this.paymentParamsHandler.handleQueryParams();
    this.route.queryParams.subscribe(params => {
      const order = params["order"];
      const idramOrder = params["EDP_BILL_NO"];
      const fastshiftOrderNumber = params["order_number"];

      if (fastshiftOrderNumber) {
        this.handleFastshiftCallback(fastshiftOrderNumber);
        return;
      }

      if (order || idramOrder) {
        const request: string = order ? order : idramOrder;
        this.initReceipt(request);
      }

      this.handleProductBuyingQueryParams(params);
    })
  }

  private initReceipt(request: string) {
    this.paymentTransactionService.getReceiptByIssuerId(request)
      .subscribe({
        next: (response: WalletToWalletPaymentReceipt) => {
          this.processIfNotWalletToWallet(request, response);
          this.paymentReceipt = response;
          this.loading = false;
          this.processPendingFeePayment();
        },
        error: (error) => {
          this.loading = false;
          if (error?.status === 404) {
            this.showSnackbar(MockupDataModel.error(`Receipt not found. Order number: ${request}. The payment may still be processing.`), SnackBarUtil.CLASSES.error);
          } else {
            this.showSnackbar(MockupDataModel.error(`${this.FAILED_PAYMENT_RESPONSE} Order number: ${request}`), SnackBarUtil.CLASSES.error);
          }
        }
      })
  }

  private handleFastshiftCallback(orderNumber: string) {
    this.fastshiftService.getStatusByNumber(orderNumber).subscribe({
      next: (order: PaymentTransactionResponse) => {
        if (order && order.orderNumber) {
          this.initReceipt(order.orderNumber);
        } else if (order && order.issuerId) {
          this.initReceipt(order.issuerId);
        } else {
          this.loading = false;
          this.showSnackbar(MockupDataModel.error(`Order not found. Order number: ${orderNumber}`), SnackBarUtil.CLASSES.error);
        }
      },
      error: () => {
        this.initReceipt(orderNumber);
      }
    });
  }


  private handleProductBuyingQueryParams(params: Params) {
    const paymentError = params['paymentError'];
    const orderId = params['orderResponse'];
    if (paymentError == 'true') {
      this.router.navigate(['error-page']).then(() => {
        this.showSnackbar(MockupDataModel.error(this.FAILED_PAYMENT_RESPONSE), SnackBarUtil.CLASSES.error);
      })
    } else if (paymentError == 'false') {
      if (orderId) {
        this.processProductBuying(orderId);
      }
    }
  }

  private processIfNotWalletToWallet(orderId: string, response: WalletToWalletPaymentReceipt) {
    if (response.type == PaymentReceiptType.WALLET_TO_WALLET) {
      this.isWalletToWallet = true;
    } else {
      this.productBuyingResponse = {
        id: response.transactionID,
        totalAmount: response.totalAmount
      } as Order;
    }
  }

  private processProductBuying(orderId: string) {
    this.isWalletToWallet = false;
    this.orderService.byId(Number(orderId)).subscribe({
      next: (orderResponse: Order) => {
        this.productBuyingResponse = orderResponse;
      },
      complete: () => {
        this.loading = false;
      }
    })
  }

  private showSnackbar(data: MockupDataModel, panelClass: string[]): void {
    this.snackBarUtil.openSnackBar({
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: panelClass,
      data: data,
    });
  }

  private hasNumber(value: string) {
    return /\d/.test(value);
  }

  private processPendingFeePayment(): void {
    if (!this.isWalletToWallet || !this.paymentReceipt) return;
    const rawContext = sessionStorage.getItem(PaymentReceiptComponent.PENDING_FEE_PAYMENT_STORAGE_KEY);
    if (!rawContext) return;

    let context: { token?: string; recordId?: number } | null = null;
    try {
      context = JSON.parse(rawContext);
    } catch {
      sessionStorage.removeItem(PaymentReceiptComponent.PENDING_FEE_PAYMENT_STORAGE_KEY);
      return;
    }

    const token = context?.token;
    const recordId = context?.recordId;
    if (!token || !recordId) {
      sessionStorage.removeItem(PaymentReceiptComponent.PENDING_FEE_PAYMENT_STORAGE_KEY);
      return;
    }

    this.feeService.payRecord(token, Number(recordId)).subscribe({
      next: () => {
        sessionStorage.removeItem(PaymentReceiptComponent.PENDING_FEE_PAYMENT_STORAGE_KEY);
      },
      error: () => {
        // Clear stale context to prevent repeated invalid attempts on future receipts.
        sessionStorage.removeItem(PaymentReceiptComponent.PENDING_FEE_PAYMENT_STORAGE_KEY);
      }
    });
  }
}
